import { get } from '@lumapps/constants';
import { PROGRESS_STATUSES } from '@lumapps/lumx-progression/constants';
import {
    mdiCheckCircle,
    mdiAlertCircle,
    mdiLock,
    mdiRadioboxBlank,
    mdiRadioboxMarked,
    mdiCloseCircle,
} from '@lumapps/lumx/icons';
import { ColorPalette } from '@lumapps/lumx/react';
import { GLOBAL } from '@lumapps/translations';
import { Button } from '@lumapps/wrex-button/components/blocks/Button';
import { EmbeddedVideo } from '@lumapps/wrex-embedded-video/components/blocks/EmbeddedVideo';
import { ImageGroup } from '@lumapps/wrex-enhanced-image/components/blocks/ImageGroup';
import { File } from '@lumapps/wrex-file/components/blocks/File';
import { Iframe } from '@lumapps/wrex-iframe/components/blocks/Iframe';
import { Link } from '@lumapps/wrex-link/components/blocks/Link';
import { ListItem } from '@lumapps/wrex-list/components/blocks/ListItem';
import { OrderedList } from '@lumapps/wrex-list/components/blocks/OrderedList';
import { UnorderedList } from '@lumapps/wrex-list/components/blocks/UnorderedList';
import { PlayVideo } from '@lumapps/wrex-play-video/components/blocks/PlayVideo';
import { QuoteBlock } from '@lumapps/wrex-quote/components/blocks/QuoteBlock';
import { Heading } from '@lumapps/wrex-typography/components/blocks/Heading';
import { Bold, Italic, Underline, Colored } from '@lumapps/wrex-typography/components/blocks/Mark';
import { Paragraph } from '@lumapps/wrex-typography/components/blocks/Paragraph';

import { LEARNING_CORE } from './keys';
import {
    ACTIVITY_STATUSES,
    MODULE_STATUSES,
    QUIZ_ANSWER_STATE,
    TRAINING_LEGACY_DATA_TYPE,
    TRAINING_TYPE,
} from './types';

const Config = get();
const orgId = Config.customerId;

export const learningApi = {
    URL: `${Config.haussmannCell}/v2/organizations/${orgId}/learning/centaurus/api`,
};

export const mcLearningApi = {
    URL: `${Config.haussmannCell}/v2/organizations/${orgId}/learning/mc/jwtlogin?redirect_page=`,
};

export const learningLegacyCTRApiUrl = learningApi.URL;

export const learningLegacyMCApiUrl = mcLearningApi.URL;

/** Feature flag used for under development features */
export const LEARNING_ALPHA_FF = 'learning-alpha';

/** Feature flag used for beta features */
export const LEARNING_BETA_FF = 'learning-beta';

/**  Feature flag used for learning specific features */
export const LEARNING_FF = 'learning';

/**  Feature flag used for display training page react */
export const LEARNING_TRAINING_PAGE_REACT = 'learning-training-page-react';

/**  Base urls for different training types */
export const TRAINING_COURSE_BASE_URL = 'training-courses';
export const SCORM_TRAINING_BASE_URL = 'scorm-trainings';
export const LEARNING_PATH_BASE_URL = 'learning-paths';
export const RECOMMENDATION_QUIZ_PATH = 'recommendation-quizzes';

export const TRAINING_TYPE_TO_PATH: Record<TRAINING_TYPE, string> = {
    [TRAINING_TYPE.TRAINING]: TRAINING_COURSE_BASE_URL,
    [TRAINING_TYPE.RECOMMENDATION_QUIZ]: RECOMMENDATION_QUIZ_PATH,
    [TRAINING_TYPE.LEARNING_PATH]: LEARNING_PATH_BASE_URL,
    [TRAINING_TYPE.SCORM_TRAINING]: SCORM_TRAINING_BASE_URL,
};

export const FILTER_TRAINING_TYPE: Record<TRAINING_TYPE | TRAINING_LEGACY_DATA_TYPE, TRAINING_TYPE> = {
    [TRAINING_TYPE.TRAINING]: TRAINING_TYPE.TRAINING,
    [TRAINING_LEGACY_DATA_TYPE.TRAINING]: TRAINING_TYPE.TRAINING,
    [TRAINING_TYPE.LEARNING_PATH]: TRAINING_TYPE.LEARNING_PATH,
    [TRAINING_LEGACY_DATA_TYPE.LEARNING_PATH]: TRAINING_TYPE.LEARNING_PATH,
    [TRAINING_TYPE.RECOMMENDATION_QUIZ]: TRAINING_TYPE.RECOMMENDATION_QUIZ,
    [TRAINING_LEGACY_DATA_TYPE.RECOMMENDATION_QUIZ]: TRAINING_TYPE.RECOMMENDATION_QUIZ,
    [TRAINING_TYPE.SCORM_TRAINING]: TRAINING_TYPE.SCORM_TRAINING,
};

export const MODULE_CONFIG = {
    icon: {
        [MODULE_STATUSES.TODO]: mdiRadioboxBlank,
        [MODULE_STATUSES.STARTED]: mdiRadioboxMarked,
        [MODULE_STATUSES.COMPLETED]: mdiCheckCircle,
        [MODULE_STATUSES.SUCCEEDED]: mdiCheckCircle,
        [MODULE_STATUSES.FAILED]: mdiAlertCircle,
        [MODULE_STATUSES.LOCKED]: mdiLock,
    },
    helper: {
        [MODULE_STATUSES.TODO]: '',
        [MODULE_STATUSES.STARTED]: '',
        [MODULE_STATUSES.COMPLETED]: '',
        [MODULE_STATUSES.SUCCEEDED]: '',
        [MODULE_STATUSES.FAILED]: LEARNING_CORE.HELPER_FAILED_MODULE,
        [MODULE_STATUSES.LOCKED]: LEARNING_CORE.HELPER_LOCKED_MODULE,
    },
    color: {
        [MODULE_STATUSES.TODO]: ColorPalette.primary,
        [MODULE_STATUSES.STARTED]: ColorPalette.primary,
        [MODULE_STATUSES.COMPLETED]: ColorPalette.primary,
        [MODULE_STATUSES.SUCCEEDED]: ColorPalette.primary,
        [MODULE_STATUSES.FAILED]: ColorPalette.yellow,
        [MODULE_STATUSES.LOCKED]: ColorPalette.grey,
    },
};

export const ACTIVITY_CONFIG = {
    label: {
        [ACTIVITY_STATUSES.TODO]: LEARNING_CORE.SCORED_ACTIVITY,
        [ACTIVITY_STATUSES.STARTED]: '',
        [ACTIVITY_STATUSES.COMPLETED]: '',
        [ACTIVITY_STATUSES.SUCCEEDED]: GLOBAL.SUCCESS,
        [ACTIVITY_STATUSES.FAILED]: GLOBAL.TRY_AGAIN,
        [ACTIVITY_STATUSES.LOCKED]: '',
    },
    color: {
        [ACTIVITY_STATUSES.TODO]: ColorPalette.grey,
        [ACTIVITY_STATUSES.STARTED]: undefined,
        [ACTIVITY_STATUSES.COMPLETED]: undefined,
        [ACTIVITY_STATUSES.SUCCEEDED]: ColorPalette.green,
        [ACTIVITY_STATUSES.FAILED]: ColorPalette.yellow,
        [ACTIVITY_STATUSES.LOCKED]: ColorPalette.grey,
    },
    progressStatus: {
        [ACTIVITY_STATUSES.TODO]: PROGRESS_STATUSES.TODO,
        [ACTIVITY_STATUSES.STARTED]: PROGRESS_STATUSES.IN_PROGRESS,
        [ACTIVITY_STATUSES.COMPLETED]: PROGRESS_STATUSES.CONFIRMED,
        [ACTIVITY_STATUSES.SUCCEEDED]: PROGRESS_STATUSES.DONE,
        [ACTIVITY_STATUSES.FAILED]: PROGRESS_STATUSES.FAILED,
        [ACTIVITY_STATUSES.LOCKED]: PROGRESS_STATUSES.TODO,
    },
};

export const QUIZ_ANSWER_CONFIG = {
    icon: {
        [QUIZ_ANSWER_STATE.CORRECT_RESULTS]: mdiCheckCircle,
        [QUIZ_ANSWER_STATE.INCORRECT_RESULTS]: mdiCloseCircle,
        [QUIZ_ANSWER_STATE.DEFAULT]: mdiRadioboxBlank,
        [QUIZ_ANSWER_STATE.NEUTRAL_SELECTED]: mdiCheckCircle,
        [QUIZ_ANSWER_STATE.NEUTRAL]: mdiRadioboxBlank,
    },
    iconColor: {
        [QUIZ_ANSWER_STATE.CORRECT_RESULTS]: ColorPalette.green,
        [QUIZ_ANSWER_STATE.INCORRECT_RESULTS]: ColorPalette.red,
        [QUIZ_ANSWER_STATE.DEFAULT]: ColorPalette.dark,
        [QUIZ_ANSWER_STATE.NEUTRAL_SELECTED]: ColorPalette.blue,
        [QUIZ_ANSWER_STATE.NEUTRAL]: undefined,
    },
    backgroundColor: {
        [QUIZ_ANSWER_STATE.CORRECT_RESULTS]: ColorPalette.green,
        [QUIZ_ANSWER_STATE.INCORRECT_RESULTS]: ColorPalette.red,
        [QUIZ_ANSWER_STATE.DEFAULT]: '',
        [QUIZ_ANSWER_STATE.NEUTRAL_SELECTED]: ColorPalette.blue,
        [QUIZ_ANSWER_STATE.NEUTRAL]: '',
    },
};
/** List of learning Wrex elements */
export const LEARNING_WREX_ELEMENTS = [
    Heading,
    Paragraph,
    QuoteBlock,
    ImageGroup,
    PlayVideo,
    File,
    Iframe,
    Button,
    EmbeddedVideo,
    Link,
    UnorderedList,
    OrderedList,
    ListItem,
];

/** List of rendered marks */
export const LEARNING_WREX_MARKS = [Bold, Italic, Underline, Colored];
